'use client'
import { useRouter, useSearchParams } from "next/navigation";
import { Dispatch, FC, ReactNode, SetStateAction, createContext, useEffect, useState } from "react";
import { constructBudgetLabel, deepEqual, parseBudget, parseCategories } from "../utils";

export type Filter = {
  label: string;
  type: FilterType;
}

export enum FilterType {
  CATEGORY = "category",
  BUDGET = "budget",
}

export interface AuctionFilterContextProps {
  // Master list, independent of type
  filters: Filter[];
  search: string;
  itemCount: number;
  setSearch: (search: string) => void;
  setItemCount: (itemCount: number) => void;
  updateFiltersOfType: (newFilters: Filter[], filterType: FilterType) => void;
  deleteFilter: (filter: Filter) => void;
  deleteFiltersOfType: (filterType: FilterType) => void;
  getFiltersOfType: (type: FilterType) => Filter[];
  clearFilters: () => void;
}

const AuctionFilterContext = createContext<AuctionFilterContextProps | null>(null);

export const AuctionFilterProvider: FC<{
  children: ReactNode
}> = ({
  children,
}) => {
    const [filters, setFilters] = useState<Filter[]>([]);
    const [search, setSearch] = useState<string>("");
    const [itemCount, setItemCount] = useState<number>(0);
    const searchParams = useSearchParams();
    const router = useRouter();

    useEffect(() => {
      const initialFilters: Filter[] = [];

      const categories = parseCategories(searchParams.get('category'));

      categories.forEach(cat => initialFilters.push({
        label: cat,
        type: FilterType.CATEGORY
      }))

      const { min: bmin, max: bmax } = parseBudget(searchParams.get('budget'));
      const budgetLabel = constructBudgetLabel(
        bmin?.toString(),
        bmax?.toString()
      )

      if (budgetLabel) {
        initialFilters.push({
          label: budgetLabel,
          type: FilterType.BUDGET
        });
      }

      setFilters(initialFilters);
    }, [searchParams]);

    const deleteFilter = (filter: Filter) => {
      const newFilters = filters.filter(f => filter.label !== f.label || filter.type !== f.type);
      setFilters(newFilters);
      updateSearchParams(newFilters);
    }

    const deleteFiltersOfType = (filterType: FilterType) => {
      const newFilters = filters.filter(f => f.type !== filterType);
      setFilters(newFilters);
      updateSearchParams(newFilters);
    }

    const updateFiltersOfType = (newFilters: Filter[], filterType: FilterType) => {
      setFilters((prevFilters) => {
        // Remove filters of the specified type
        const filteredFilters = prevFilters.filter(f => f.type !== filterType);

        // Add the new filters for this type
        const updatedFilters = [...filteredFilters, ...newFilters];

        // Update search params and state
        updateSearchParams(updatedFilters);
        return updatedFilters;
      });
    };

    const updateSearchParams = (newFilters: Filter[]): void => {
      const params = new URLSearchParams(window.location.search);

      const groupedFilters = newFilters.reduce((acc, filter) => {
        acc[filter.type] = acc[filter.type] || [];
        acc[filter.type].push(filter.label);
        return acc;
      }, {} as Record<string, string[]>);

      Object.values(FilterType).forEach((ftype) => {
        const filterKey = ftype.toLowerCase();
        const filterString = groupedFilters[filterKey]?.join(',');

        if (filterString) {
          params.set(filterKey, filterString);
        } else {
          params.delete(filterKey);
        }
      });

      const paramString = params.toString();
      const query = paramString ? "?" + paramString : "";
      router.replace(window.location.pathname + query);
    }

    const getFiltersOfType = (type: FilterType) => {
      return filters.filter(filter => filter.type === type);
    }

    const clearFilters = () => {
      setFilters([]);
      updateSearchParams([]);
    }

    // The value that will be given to the context
    const value = {
      deleteFilter,
      deleteFiltersOfType,
      filters,
      updateFiltersOfType,
      getFiltersOfType,
      clearFilters,
      search,
      setSearch,
      itemCount,
      setItemCount
    };

    // Correctly formatting your JSX
    return (
      <AuctionFilterContext.Provider value={value}>
        {children}
      </AuctionFilterContext.Provider>
    );
  };

export { AuctionFilterContext };
